import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, LogIn, Clock, ArrowRight, LogOut, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUserStore } from '../store/userStore';
import { authHeaders } from '../utils/api';
import {SERVER_URL} from "../config";
import Header from "../components/Layout/Header";

const Lobby = () => {
  const [roomCode, setRoomCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [myRooms, setMyRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('zul');
  const navigate = useNavigate();

  const userId = useUserStore((state) => state.userId);
  const userName = useUserStore((state) => state.userName);
  const clearUser = useUserStore((state) => state.clearUser);

  const languages = [
    { code: 'zul', name: 'isiZulu' },
    { code: 'eng', name: 'English' },
    { code: 'afr', name: 'Afrikaans' },
    { code: 'nso', name: 'Sepedi' },
    { code: 'sot', name: 'Sesotho' },
    { code: 'tsn', name: 'Setswana' },
    { code: 'xho', name: 'isiXhosa' },
    { code: 'ven', name: 'Tshivenda' },
    { code: 'tso', name: 'Xitsonga' },
    { code: 'ssw', name: 'siSwati' },
    { code: 'nbl', name: 'isiNdebele' },
  ];

  useEffect(() => {
    if (!userId) {
      toast.error('User not authenticated');
      navigate('/');
      return;
    }
    fetchMyRooms();
  }, [userId, navigate]);

  const fetchMyRooms = async () => {
    setLoadingRooms(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/me/rooms`, {
        headers: { ...authHeaders() },
      });
      if (res.ok) {
        const data = await res.json();
        setMyRooms(data.rooms || []);
      }
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    } finally {
      setLoadingRooms(false);
    }
  };

  const generateCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleCreateRoom = async () => {
    if (!userId) {
      toast.error('User ID not found. Please login again.');
      navigate('/');
      return;
    }

    const code = generateCode();
    setLoading(true);

    try {
      const res = await fetch(`${SERVER_URL}/api/rooms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify({
          code,
          lang_code: selectedLanguage
        })
      });

      if (res.ok) {
        toast.success(`Room ${code} created!`);
        setShowCreateModal(false);
        navigate(`/room/${code}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create room');
      }
    } catch (error) {
      console.error('Failed to create room:', error);
      toast.error('Failed to create room. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinRoom = async (e) => {
    e.preventDefault();
    if (!roomCode.trim()) {
      toast.error('Please enter a room code');
      return;
    }

    if (!userId) {
      toast.error('User ID not found. Please login again.');
      navigate('/');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/rooms/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify({ code: roomCode.trim().toUpperCase() })
      });

      if (res.ok) {
        toast.success('Joined room successfully!');
        navigate(`/room/${roomCode.trim().toUpperCase()}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to join room');
      }
    } catch (error) {
      console.error('Failed to join room:', error);
      toast.error('Failed to join room. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRejoinRoom = (code) => {
    toast.loading('Rejoining room...', { id: 'rejoin' });
    setTimeout(() => {
      toast.success('Rejoined room!', { id: 'rejoin' });
      navigate(`/room/${code}`);
    }, 500);
  };

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to logout?')) return;

    try {
      // Notify server to revoke session and clean up user data
      await fetch(`${SERVER_URL}/api/logout`, {
        method: 'POST',
        headers: { ...authHeaders() },
      });
    } catch (err) {
      console.error('Logout request failed (session may remain active):', err);
    }

    clearUser();
    toast.success('Logged out successfully');
    navigate('/');
  };

return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <Header
        userName={userName}
        onLogout={handleLogout}
        showLogout={true}
      />

      {/* Main Content */}
      <div className="flex-1 p-4">
        <div className="max-w-6xl mx-auto pt-8">
          {/* Welcome Section */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              Welcome, {userName}
            </h1>
            <p className="text-lg text-gray-600">Create or join a conversation</p>
          </div>

          {/* Voice Lab Feature Card */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 p-6 rounded-2xl mb-6 shadow-sm">
            <div className="flex items-center justify-between flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    Try Voice Lab ✨
                  </h3>
                  <p className="text-gray-700 text-sm">
                    Experiment with real-time voice transcription — no room needed!
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/voice-lab')}
                className="px-6 py-3 rounded-lg font-semibold transition-all bg-orange-600 hover:bg-orange-700 text-white shadow-md uppercase text-sm whitespace-nowrap"
              >
                Launch Lab
              </button>
            </div>
          </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-200">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center mb-6">
              <Plus className="w-6 h-6 text-white" />
            </div>

            <h2 className="text-2xl font-bold mb-3 text-gray-900">Create Room</h2>
            <p className="text-gray-600 mb-6">Start a new conversation</p>

            <button
              onClick={() => setShowCreateModal(true)}
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors uppercase disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create New Room
            </button>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-200">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-700 rounded-xl flex items-center justify-center mb-6">
              <LogIn className="w-6 h-6 text-white" />
            </div>

            <h2 className="text-2xl font-bold mb-3 text-gray-900">Join Room</h2>
            <p className="text-gray-600 mb-6">Enter a room code</p>

            <form onSubmit={handleJoinRoom} className="space-y-4">
              <input
                  name={"join"}
                type="text"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-900 placeholder-gray-400 font-mono text-lg"
                placeholder="XXXXXX"
                maxLength={10}
              />

              <button
                type="submit"
                disabled={loading || !roomCode.trim()}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors uppercase disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Joining...' : 'Join Room'}
              </button>
            </form>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <Clock className="w-6 h-6 text-orange-600" />
            <h2 className="text-2xl font-bold text-gray-900">My Rooms</h2>
          </div>

          {loadingRooms ? (
            <div className="text-center py-8">
              <div className="inline-block w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600 mt-4">Loading your rooms...</p>
            </div>
          ) : myRooms.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No rooms yet. Create your first room!</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {myRooms.map((room) => (
                <div
                  key={room.code}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-all group"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-lg font-semibold text-gray-900">{room.code}</span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        room.active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {room.active ? 'Active' : 'Ended'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Created {new Date(room.created_at).toLocaleString()}
                    </p>
                    {room.participants && room.participants.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {room.participants.length} participant(s)
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => handleRejoinRoom(room.code)}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 font-medium rounded-lg transition-all group-hover:translate-x-1"
                  >
                    <span className="text-sm">Rejoin</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>

      {/* Create Room Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white p-8 rounded-2xl max-w-md w-full shadow-xl border border-gray-200">
            <h3 className="text-2xl font-bold mb-2 text-gray-900">Create Room</h3>
            <p className="text-gray-600 mb-6">Select the transcription language</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transcription Language
                </label>
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900"
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code} className="bg-white">
                      {lang.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">
                  This language will be used for speech transcription
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRoom}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg transition-colors uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create Room'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lobby;